import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@itaas/core/auth/useAuth";
import { ServiceBillingBadge } from "@/components/billing/ServiceBillingBadge";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import {
  ADMISSION_STATUS_LABELS,
  type Admission,
} from "@/core/admissions/types";
import { getHospitalRevenueSettings } from "@/core/admin/establishment-store";
import {
  billingSnapshotForAdmission,
  billingSnapshotForSurgeryCase,
} from "@/core/billing/service-billing-status";
import { canAccessClinicalSection } from "@/core/permissions";
import { getPatientById, patientDetailPath } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { listSurgeryOrientations } from "@/core/services/service-admission-queries";
import {
  COMMON_SURGERY_PROCEDURES,
  completeSurgeryCase,
  createSurgeryCase,
  getSurgeryCaseByAdmission,
  listSurgeryCases,
  SURGERY_CASE_STATUS_LABELS,
  SurgeryCaseValidationError,
  updateSurgeryCase,
  type SurgeryCase,
} from "@/core/surgery/surgery-case-store";
import { routes } from "@/router/all_routes";

export function SurgeryPage() {
  const auth = useAuth();
  const canWrite = canAccessClinicalSection(auth);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedAdmissionId, setSelectedAdmissionId] = useState("");
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [procedureName, setProcedureName] = useState(COMMON_SURGERY_PROCEDURES[0]);
  const [surgeonName, setSurgeonName] = useState("");
  const [durationMinutes, setDurationMinutes] = useState("");
  const [procedureFee, setProcedureFee] = useState(
    String(getHospitalRevenueSettings().surgeryProcedureFee),
  );
  const [operativeNotes, setOperativeNotes] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const refresh = () => setRefreshKey((k) => k + 1);
    window.addEventListener("hospitol:admissions", refresh);
    window.addEventListener("hospitol:billing-documents", refresh);
    window.addEventListener("hospitol:surgery", refresh);
    return () => {
      window.removeEventListener("hospitol:admissions", refresh);
      window.removeEventListener("hospitol:billing-documents", refresh);
      window.removeEventListener("hospitol:surgery", refresh);
    };
  }, []);

  const orientations = useMemo(() => {
    void refreshKey;
    return listSurgeryOrientations();
  }, [refreshKey]);

  const cases = useMemo(() => {
    void refreshKey;
    return listSurgeryCases();
  }, [refreshKey]);

  const rows = useMemo(() => {
    const caseByAdmission = new Map(cases.map((c) => [c.admissionId, c]));
    const orientationRows = orientations
      .filter((a) => !caseByAdmission.has(a.id))
      .map((admission) => ({ kind: "orientation" as const, admission, surgeryCase: null }));
    const caseRows = cases.map((surgeryCase) => {
      const admission = orientations.find((a) => a.id === surgeryCase.admissionId) ?? null;
      return { kind: "case" as const, admission, surgeryCase };
    });
    return [...caseRows, ...orientationRows];
  }, [orientations, cases]);

  const pagination = useClientPagination(rows, [], 25);
  const { pagedItems } = pagination;

  const selectedCase = selectedCaseId
    ? cases.find((c) => c.id === selectedCaseId) ?? null
    : selectedAdmissionId
      ? getSurgeryCaseByAdmission(selectedAdmissionId)
      : null;

  const selectedAdmission =
    orientations.find((a) => a.id === (selectedCase?.admissionId ?? selectedAdmissionId)) ?? null;

  useEffect(() => {
    if (selectedCase) {
      setSelectedCaseId(selectedCase.id);
      setProcedureName(selectedCase.procedureName);
      setSurgeonName(selectedCase.surgeonName);
      setDurationMinutes(
        selectedCase.durationMinutes > 0 ? String(selectedCase.durationMinutes) : "",
      );
      setProcedureFee(String(selectedCase.procedureFee));
      setOperativeNotes(selectedCase.operativeNotes);
    }
  }, [selectedCase?.id, selectedCase?.updatedAt]);

  const flash = (text: string) => {
    setMsg(text);
    window.setTimeout(() => setMsg(null), 2800);
  };

  const refresh = () => setRefreshKey((k) => k + 1);

  const handleCreateCase = (e: FormEvent) => {
    e.preventDefault();
    if (!canWrite || !selectedAdmissionId) return;
    setError(null);
    try {
      const created = createSurgeryCase({
        admissionId: selectedAdmissionId,
        procedureName,
        surgeonName: surgeonName || undefined,
        durationMinutes: Number(durationMinutes) || 0,
        procedureFee: Number(procedureFee) || 0,
        operativeNotes,
      });
      setSelectedCaseId(created.id);
      flash("Dossier bloc créé.");
      refresh();
    } catch (err) {
      setError(err instanceof SurgeryCaseValidationError ? err.message : "Création impossible.");
    }
  };

  const handleSaveCase = (e: FormEvent) => {
    e.preventDefault();
    if (!canWrite || !selectedCase) return;
    setError(null);
    try {
      updateSurgeryCase(selectedCase.id, {
        procedureName,
        surgeonName,
        durationMinutes: Number(durationMinutes) || 0,
        procedureFee: Number(procedureFee) || 0,
        operativeNotes,
        status: selectedCase.status === "scheduled" ? "in_progress" : selectedCase.status,
      });
      flash("Dossier enregistré.");
      refresh();
    } catch (err) {
      setError(err instanceof SurgeryCaseValidationError ? err.message : "Enregistrement impossible.");
    }
  };

  const handleComplete = async () => {
    if (!canWrite || !selectedCase) return;
    setError(null);
    try {
      await completeSurgeryCase(selectedCase.id);
      flash("Acte clôturé — facturation émise.");
      refresh();
    } catch (err) {
      setError(err instanceof SurgeryCaseValidationError ? err.message : "Clôture impossible.");
    }
  };

  const openOrientation = (admissionId: string) => {
    setSelectedAdmissionId(admissionId);
    setSelectedCaseId("");
    setProcedureName(COMMON_SURGERY_PROCEDURES[0]);
    setSurgeonName("");
    setDurationMinutes("");
    setProcedureFee(String(getHospitalRevenueSettings().surgeryProcedureFee));
    setOperativeNotes("");
  };

  const openCase = (surgeryCase: SurgeryCase) => {
    setSelectedCaseId(surgeryCase.id);
    setSelectedAdmissionId(surgeryCase.admissionId);
  };

  return (
    <div>
      <PageHeader
        title="Chirurgie & bloc opératoire"
        description="Dossiers bloc, saisie d'acte et facturation détaillée — en complément du forfait d'orientation."
        actions={
          <div className="ph-page-header-actions">
            <Link to={routes.admissions} className="btn btn-outline-secondary btn-sm">
              File accueil
            </Link>
          </div>
        }
      />

      {msg && <div className="alert alert-success py-2 small">{msg}</div>}
      {error && <div className="alert alert-warning py-2 small">{error}</div>}

      <div className="row g-3 ph-split-workspace">
        <div className="col-lg-7">
          <div className="ph-invoice-sheet">
            <p className="small text-muted mb-3">
              <strong>Orientation</strong> : forfait auto à l&apos;accueil.{" "}
              <strong>Acte bloc</strong> : intervention, chirurgien, durée et tarif à la clôture.
            </p>
            {rows.length === 0 ? (
              <p className="text-muted small mb-0">
                Aucune orientation chirurgie — orientez un patient vers &quot;Chirurgie / bloc&quot;.
              </p>
            ) : (
              <div className="table-responsive ph-diag-table">
                <table className="table table-sm ph-dash-table mb-0">
                  <thead>
                    <tr>
                      <th>Patient</th>
                      <th>Intervention / motif</th>
                      <th>Étape</th>
                      <th>Facturation</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {pagedItems.map((row) => {
                      const admission = row.admission;
                      const surgeryCase = row.surgeryCase;
                      const patient = admission
                        ? getPatientById(admission.patientId)
                        : surgeryCase
                          ? getPatientById(surgeryCase.patientId)
                          : null;
                      const orientBilling = admission
                        ? billingSnapshotForAdmission(admission.id, "surgery")
                        : null;
                      const actBilling = surgeryCase
                        ? billingSnapshotForSurgeryCase(surgeryCase.id)
                        : null;

                      return (
                        <tr key={surgeryCase?.id ?? admission?.id} className="ph-diag-row">
                          <td data-label="Patient" className="fw-medium">
                            {patient ? (
                              <Link to={patientDetailPath(patient.id)}>
                                {patientFullName(patient)}
                              </Link>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td data-label="Intervention" className="small">
                            {surgeryCase ? (
                              <>
                                <div className="fw-semibold">{surgeryCase.procedureName}</div>
                                <div className="text-muted">{surgeryCase.surgeonName}</div>
                              </>
                            ) : (
                              admission?.motif ?? "—"
                            )}
                          </td>
                          <td data-label="Étape">
                            {surgeryCase ? (
                              <span className="badge text-bg-light border">
                                {SURGERY_CASE_STATUS_LABELS[surgeryCase.status]}
                              </span>
                            ) : admission ? (
                              <span className="badge text-bg-light border">
                                {ADMISSION_STATUS_LABELS[admission.status]}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td data-label="Facturation">
                            <div className="d-flex flex-column align-items-start gap-1">
                              {orientBilling?.hasDocument && (
                                <span className="small text-muted">Orientation</span>
                              )}
                              {orientBilling && <ServiceBillingBadge snapshot={orientBilling} compact />}
                              {actBilling?.hasDocument && (
                                <span className="small text-muted mt-1">Acte bloc</span>
                              )}
                              {actBilling && <ServiceBillingBadge snapshot={actBilling} compact />}
                              {!orientBilling?.hasDocument && !actBilling?.hasDocument && (
                                <ServiceBillingBadge
                                  snapshot={{
                                    documentId: null,
                                    status: null,
                                    statusLabel: "Non facturé",
                                    amount: null,
                                    hasDocument: false,
                                  }}
                                  compact
                                />
                              )}
                            </div>
                          </td>
                          <td data-label="Action" className="text-end">
                            {surgeryCase ? (
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => openCase(surgeryCase)}
                              >
                                Ouvrir
                              </button>
                            ) : admission ? (
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => openOrientation(admission.id)}
                              >
                                Créer acte
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <TablePagination
                  page={pagination.page}
                  pageSize={pagination.pageSize}
                  totalItems={pagination.totalItems}
                  onPageChange={pagination.setPage}
                  onPageSizeChange={pagination.setPageSize}
                />
              </div>
            )}
          </div>
        </div>

        <div className="col-lg-5">
          <div className="ph-invoice-sheet">
            <h3 className="h6 fw-semibold mb-3">Saisie acte bloc</h3>
            {!canWrite && (
              <p className="small text-muted">Lecture seule — droits cliniques requis.</p>
            )}

            {selectedAdmission && !selectedCase && canWrite && (
              <form onSubmit={handleCreateCase} className="row g-2">
                <div className="col-12">
                  <p className="small mb-2">
                    Nouveau dossier pour{" "}
                    <strong>
                      {patientFullName(getPatientById(selectedAdmission.patientId) ?? {
                        firstName: "?",
                        lastName: "",
                      })}
                    </strong>
                  </p>
                </div>
                <SurgeryCaseFormFields
                  procedureName={procedureName}
                  surgeonName={surgeonName}
                  durationMinutes={durationMinutes}
                  procedureFee={procedureFee}
                  operativeNotes={operativeNotes}
                  onProcedureName={setProcedureName}
                  onSurgeonName={setSurgeonName}
                  onDurationMinutes={setDurationMinutes}
                  onProcedureFee={setProcedureFee}
                  onOperativeNotes={setOperativeNotes}
                />
                <div className="col-12">
                  <button type="submit" className="btn btn-primary btn-sm">
                    Créer le dossier bloc
                  </button>
                </div>
              </form>
            )}

            {selectedCase && selectedCase.status !== "completed" && canWrite && (
              <form onSubmit={handleSaveCase} className="row g-2">
                <div className="col-12">
                  <p className="small text-muted mb-1">
                    {selectedCase.caseNumber} · {selectedCase.procedureName}
                  </p>
                </div>
                <SurgeryCaseFormFields
                  procedureName={procedureName}
                  surgeonName={surgeonName}
                  durationMinutes={durationMinutes}
                  procedureFee={procedureFee}
                  operativeNotes={operativeNotes}
                  onProcedureName={setProcedureName}
                  onSurgeonName={setSurgeonName}
                  onDurationMinutes={setDurationMinutes}
                  onProcedureFee={setProcedureFee}
                  onOperativeNotes={setOperativeNotes}
                />
                <div className="col-12 d-flex gap-2 flex-wrap ph-split-actions">
                  <button type="submit" className="btn btn-outline-primary btn-sm">
                    Enregistrer
                  </button>
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={() => void handleComplete()}
                  >
                    Clôturer & facturer l&apos;acte
                  </button>
                </div>
              </form>
            )}

            {selectedCase?.status === "completed" && (
              <div className="small">
                <p className="mb-2">
                  <strong>{selectedCase.caseNumber}</strong> — acte terminé le{" "}
                  {selectedCase.completedAt
                    ? new Date(selectedCase.completedAt).toLocaleString("fr-FR")
                    : "—"}
                </p>
                <p className="text-muted mb-2">
                  {selectedCase.procedureName} · {selectedCase.surgeonName}
                  {selectedCase.durationMinutes > 0
                    ? ` · ${selectedCase.durationMinutes} min`
                    : ""}
                </p>
                <ServiceBillingBadge snapshot={billingSnapshotForSurgeryCase(selectedCase.id)} />
              </div>
            )}

            {!selectedAdmission && !selectedCase && (
              <p className="small text-muted mb-0">
                Sélectionnez une orientation ou un dossier dans la liste.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SurgeryCaseFormFields({
  procedureName,
  surgeonName,
  durationMinutes,
  procedureFee,
  operativeNotes,
  onProcedureName,
  onSurgeonName,
  onDurationMinutes,
  onProcedureFee,
  onOperativeNotes,
}: {
  procedureName: string;
  surgeonName: string;
  durationMinutes: string;
  procedureFee: string;
  operativeNotes: string;
  onProcedureName: (v: string) => void;
  onSurgeonName: (v: string) => void;
  onDurationMinutes: (v: string) => void;
  onProcedureFee: (v: string) => void;
  onOperativeNotes: (v: string) => void;
}) {
  return (
    <>
      <div className="col-12">
        <label className="form-label small">Intervention</label>
        <select
          className="form-select form-select-sm"
          value={procedureName}
          onChange={(e) => onProcedureName(e.target.value)}
        >
          {COMMON_SURGERY_PROCEDURES.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label small">Chirurgien</label>
        <input
          className="form-control form-control-sm"
          value={surgeonName}
          onChange={(e) => onSurgeonName(e.target.value)}
          placeholder="Dr. …"
        />
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label small">Durée (min)</label>
        <input
          type="number"
          min={0}
          className="form-control form-control-sm"
          value={durationMinutes}
          onChange={(e) => onDurationMinutes(e.target.value)}
        />
      </div>
      <div className="col-12">
        <label className="form-label small">Tarif acte (FC)</label>
        <input
          type="number"
          min={0}
          className="form-control form-control-sm"
          value={procedureFee}
          onChange={(e) => onProcedureFee(e.target.value)}
        />
      </div>
      <div className="col-12">
        <label className="form-label small">Notes opératoires</label>
        <textarea
          className="form-control form-control-sm"
          rows={3}
          value={operativeNotes}
          onChange={(e) => onOperativeNotes(e.target.value)}
        />
      </div>
    </>
  );
}
